import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart3, PiggyBank, Sparkles, TrendingUp } from 'lucide-react';
import {
  ActionButton,
  ContextHero,
  DataCard,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
  StatusBadge,
  type UniversalStatus,
} from '@/components/design-system';
import { BenefitSimulatorCard } from '@/components/design-system/BenefitSimulatorCard';
import { useFormationB2BKpis, useFormationB2BRoi } from '@/hooks/queries/useFormationB2B';

const STATUT_TONE: Record<string, UniversalStatus> = {
  brouillon: 'brouillon',
  en_cours: 'en_cours',
  realise: 'planifie',
  facture: 'en_attente',
  encaisse: 'planifie',
  solde: 'paye',
  annule: 'annule',
};

interface SimuCharge {
  categorie: string;
  total: number;
}

const DEFAULT_CHARGES: SimuCharge[] = [
  { categorie: 'honoraires_formateur', total: 100000 },
  { categorie: 'hotel_lieu', total: 50000 },
  { categorie: 'traiteur', total: 20000 },
  { categorie: 'transport', total: 0 },
  { categorie: 'supports_impressions', total: 5000 },
  { categorie: 'commission_commercial', total: 0 },
  { categorie: 'marketing_digital', total: 0 },
  { categorie: 'autre', total: 0 },
];

const CAT_LABEL: Record<string, string> = {
  honoraires_formateur: 'Honoraires formateur',
  hotel_lieu: 'Hôtel / Lieu',
  traiteur: 'Traiteur',
  transport: 'Transport',
  supports_impressions: 'Supports / Impressions',
  commission_commercial: 'Commission commercial',
  marketing_digital: 'Marketing digital',
  autre: 'Autre',
};

export function RoiTab(): JSX.Element {
  const { data: k, isLoading: kpisLoading } = useFormationB2BKpis();
  const { data: roi = [], isLoading } = useFormationB2BRoi();
  const [mode, setMode] = useState<'liste' | 'simulateur'>('liste');

  // Simulateur state
  const [prixVenteHT, setPrixVenteHT] = useState(450000);
  const [charges, setCharges] = useState<SimuCharge[]>(DEFAULT_CHARGES);
  const totalCharges = useMemo(() => charges.reduce((s, c) => s + c.total, 0), [charges]);

  const totalCa = roi.reduce((s, r) => s + Number(r.prix_vente_ht), 0);
  const totalCharges30j = roi.reduce((s, r) => s + Number(r.total_charges), 0);
  const totalBenefice = roi.reduce((s, r) => s + Number(r.benefice_brut), 0);
  const margeMoyenne =
    roi.length > 0 ? roi.reduce((s, r) => s + Number(r.marge_pct), 0) / roi.length : 0;

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="formation_b2b"
          icon={<BarChart3 className="h-5 w-5" />}
          title="ROI & Simulateur"
          subtitle="Bénéfice net par session, marge consolidée et simulateur de rentabilité avant vente."
          actions={
            <div className="flex overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
              <button
                onClick={() => setMode('liste')}
                className={
                  'px-3 py-1.5 text-xs font-semibold transition-colors ' +
                  (mode === 'liste'
                    ? 'bg-violet-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50')
                }
              >
                Liste sessions
              </button>
              <button
                onClick={() => setMode('simulateur')}
                className={
                  'px-3 py-1.5 text-xs font-semibold transition-colors ' +
                  (mode === 'simulateur'
                    ? 'bg-violet-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50')
                }
              >
                Simulateur
              </button>
            </div>
          }
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            variant="formation_b2b"
            label="CA total réalisé"
            value={<MoneyAmount amount={totalCa} variant="inline" />}
            icon={<TrendingUp className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="finances"
            label="Charges totales"
            value={<MoneyAmount amount={totalCharges30j} variant="inline" />}
            loading={isLoading}
          />
          <KpiCard
            variant="formation_b2b"
            label="Bénéfice net 30j"
            value={
              <MoneyAmount
                amount={k?.benefice_net_30j ?? totalBenefice}
                variant="inline"
                withColor
              />
            }
            icon={<PiggyBank className="h-4 w-4" />}
            loading={kpisLoading}
          />
          <KpiCard
            variant="formation_b2b"
            label="Marge moyenne"
            value={`${margeMoyenne.toFixed(1)}%`}
            icon={<Sparkles className="h-4 w-4" />}
            loading={isLoading}
          />
        </div>

        {mode === 'simulateur' ? (
          <>
            <SectionTitle>Simulateur de bénéfice</SectionTitle>
            <div className="grid gap-5 lg:grid-cols-2">
              <DataCard title="Hypothèses">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    Prix de vente HT (DA)
                  </span>
                  <input
                    type="number"
                    value={prixVenteHT}
                    onChange={(e) => setPrixVenteHT(Number(e.target.value))}
                    className="input mt-1"
                  />
                </label>
                <div className="mt-3 space-y-2">
                  {charges.map((c, idx) => (
                    <div key={c.categorie} className="grid grid-cols-[1fr_auto] items-center gap-2">
                      <span className="text-xs text-slate-700">{CAT_LABEL[c.categorie]}</span>
                      <input
                        type="number"
                        value={c.total}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setCharges((cs) =>
                            cs.map((cc, i) => (i === idx ? { ...cc, total: v } : cc)),
                          );
                        }}
                        className="h-8 w-28 rounded-lg border border-slate-200 bg-white px-2 text-right text-xs tabular-nums"
                      />
                    </div>
                  ))}
                </div>
              </DataCard>

              <BenefitSimulatorCard
                prixVenteHT={prixVenteHT}
                totalCharges={totalCharges}
                charges={charges}
                variant="formation_b2b"
              />
            </div>
          </>
        ) : isLoading ? (
          <SkeletonShimmer variant="table" count={5} />
        ) : roi.length === 0 ? (
          <EmptyStateModern
            variant="formation_b2b"
            icon={<BarChart3 className="h-7 w-7" />}
            title="Aucun dossier ROI"
            description="Les sessions B2B avec charges saisies apparaissent ici avec leur bénéfice et marge."
            ctaLabel="Lancer le simulateur"
            onCtaClick={() => setMode('simulateur')}
          />
        ) : (
          <DataCard>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="py-2">Dossier</th>
                    <th className="py-2">Client</th>
                    <th className="py-2">Formateur</th>
                    <th className="py-2 text-right">Vente HT</th>
                    <th className="py-2 text-right">Charges</th>
                    <th className="py-2 text-right">Bénéfice</th>
                    <th className="py-2 text-right">Marge</th>
                    <th className="py-2">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {roi.map((r, i) => (
                    <motion.tr
                      key={r.dossier_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(i, 8) * 0.02 }}
                      className="border-t border-slate-100 hover:bg-slate-50"
                    >
                      <td className="py-2">
                        <Link
                          to={`/dossiers/${r.dossier_id}`}
                          className="font-mono text-violet-700 hover:underline"
                        >
                          {r.dossier_reference}
                        </Link>
                      </td>
                      <td className="py-2 font-semibold text-slate-800">{r.client_nom ?? '—'}</td>
                      <td className="py-2 text-slate-600">
                        {r.formateur_prenom} {r.formateur_nom}
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={r.prix_vente_ht} variant="inline" />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={r.total_charges} variant="inline" />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={r.benefice_brut} variant="inline" withColor />
                      </td>
                      <td className="py-2 text-right">
                        <span
                          className={
                            'font-bold tabular-nums ' +
                            (Number(r.marge_pct) >= 30
                              ? 'text-emerald-700'
                              : Number(r.marge_pct) >= 15
                                ? 'text-amber-700'
                                : 'text-rose-700')
                          }
                        >
                          {Number(r.marge_pct).toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-2">
                        <StatusBadge
                          status={STATUT_TONE[r.statut_global] ?? 'brouillon'}
                          label={r.statut_global}
                        />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataCard>
        )}

        {mode === 'liste' && roi.length > 0 && (
          <div className="flex justify-end">
            <ActionButton
              variant="gradient"
              module="formation_b2b"
              onClick={() => setMode('simulateur')}
            >
              <Sparkles className="h-3.5 w-3.5" /> Simuler une nouvelle vente
            </ActionButton>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
